import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Location } from '@angular/common';
import { CompanyService } from '../../services/company.service';
import { filter, map } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { LogOutDialog } from './dialog/dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MiscService } from '../../services/misc.service';
import { combineLatest, firstValueFrom } from 'rxjs';
import { TransactionService } from '../../services/transaction.service';
import { SmartphotoService } from '../../services/smartphoto.service';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss']
})
export class NavbarComponent implements OnInit {
  private readonly companyService = inject(CompanyService);
  private readonly smartphotoService = inject(SmartphotoService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly miscService = inject(MiscService);
  private readonly transactionService = inject(TransactionService);
  private readonly location = inject(Location);

  public customLogo?: string;
  public defaultLogo = false;
  public showGoBack = false;
  public showSecondaryActions = false;
  public loading = true;
  public openedSecondaryActions = false;
  public actionsDisabled$ = combineLatest([this.transactionService.queueStatus$, this.smartphotoService.abilities.isWaitingForAI$]).pipe(
    map(([queueStatus, isWaitingForAI]) => queueStatus || isWaitingForAI)
  );

  public async ngOnInit(): Promise<void> {
    const fromRemote = this.miscService.isRemote();

    if (fromRemote) {
      this.showGoBack = true;
      this.showSecondaryActions = true;
    }

    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event: unknown) => event instanceof NavigationEnd)
      )
      .subscribe((event) => {
        this.showGoBack = !((<NavigationEnd>event).url === '/home') && this.location.path() !== '/terms-and-conditions';
        this.showSecondaryActions = !((<NavigationEnd>event).url === '/create');
      });

    this.companyService
      .getCurrentCompanyObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (company) => {
        if (!company) {
          return;
        }

        this.customLogo = this.companyService.getNavBarLogo();

        if (!this.customLogo) {
          this.defaultLogo = true;
        }

        this.loading = false;
      });
  }

  public async goToHome(): Promise<void> {
    await this.router.navigate([`/home`]);
  }

  public async goBack(): Promise<void> {
    const isSecondaryActionActive = await firstValueFrom(this.miscService.secondaryActionsActive$);

    if (isSecondaryActionActive) {
      this.miscService.toggleSecondaryActions();
    }

    await this.goToHome();
  }

  public async openLogOutDialog(): Promise<void> {
    this.dialog.open(LogOutDialog, {
      width: 'calc(100% - 40px)',
      maxWidth: '600px'
    });
  }

  public openSecondaryActions(): void {
    if (!this.transactionService.empty) {
      return;
    }

    this.miscService.toggleSecondaryActions();
  }
}
